import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useToast } from "@/hooks/use-toast";
import { createProspect, type FinalClientType } from "@/lib/prospectsApi";
import { finalClientTypeOptions } from "@/components/prospects/prospectConstants";
import { COUNTRY_OPTIONS } from "@/lib/countries";
import { Loader2, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { lookupUsersByRoles } from "@/lib/usersApi";

const prospectSchema = z.object({
  company_name: z.string().min(1, "Company name is required.").max(200),
  business_type: z.enum(["operator", "agency", "final_client"], {
    required_error: "Business type is required.",
  }),
  final_client_types: z.array(
    z.enum([
      "aviation",
      "beauty",
      "corporate",
      "direct_consumer",
      "esg",
      "event",
      "fashion",
      "food_and_beverage",
      "government",
      "health",
      "marine",
      "mice",
      "sports",
      "trade_show",
    ])
  ).optional(),
  country: z.string().max(100).optional().or(z.literal("")),
  address: z.string().max(255).optional().or(z.literal("")),
  website: z.string().max(255).optional().or(z.literal("")),
  referred_from: z.string().max(100).optional().or(z.literal("")),
  owner_id: z.string().optional(),
  contact_name: z.string().max(200).optional().or(z.literal("")),
  contact_position: z.string().max(100).optional().or(z.literal("")),
  contact_email: z.string().email("Invalid email").optional().or(z.literal("")),
  contact_telephone: z.string().max(255).optional().or(z.literal("")),
}).superRefine((data, ctx) => {
  if (data.business_type === "final_client" && (data.final_client_types == null || data.final_client_types.length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "At least one Final Client Type is required.",
      path: ["final_client_types"],
    });
  }
});

type ProspectFormData = z.infer<typeof prospectSchema>;

interface ProspectFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const ProspectForm = ({ open, onOpenChange, onSuccess }: ProspectFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [finalClientTypePicker, setFinalClientTypePicker] = useState<FinalClientType | "">("");
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownerOptions, setOwnerOptions] = useState<Array<{ id: string; full_name: string; email?: string }>>([]);
  const { toast } = useToast();
  const { userRole } = useAuth();
  const canAssignOwner = userRole === "ceo" || userRole === "general_manager";

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ProspectFormData>({
    resolver: zodResolver(prospectSchema),
    defaultValues: {
      business_type: "agency",
      final_client_types: [],
      country: "",
      website: "",
      owner_id: "unassigned",
    },
  });

  useEffect(() => {
    register("business_type");
    register("final_client_types");
    register("owner_id");
    register("country");
  }, [register]);

  useEffect(() => {
    if (open) {
      reset({
        business_type: "agency",
        final_client_types: [],
        country: "",
        website: "",
        owner_id: "unassigned",
      });
      setFinalClientTypePicker("");
    }
  }, [open, reset]);

  const selectedBusinessType = watch("business_type") || "agency";
  const selectedCountry = watch("country") || "";
  const finalClientTypes = watch("final_client_types") || [];

  const addFinalClientType = (value: FinalClientType) => {
    const next = Array.from(new Set([...(finalClientTypes || []), value])) as FinalClientType[];
    setValue("final_client_types", next, { shouldDirty: true, shouldValidate: true });
    setFinalClientTypePicker("");
  };

  const removeFinalClientType = (value: FinalClientType) => {
    const next = (finalClientTypes || []).filter((item) => item !== value) as FinalClientType[];
    setValue("final_client_types", next, { shouldDirty: true, shouldValidate: true });
  };

  useEffect(() => {
    if (!open || !canAssignOwner) return;
    const loadOwners = async () => {
      try {
        setOwnersLoading(true);
        const users = await lookupUsersByRoles(["sales_staff", "general_manager", "ceo"]);
        const normalized = (users || []).map((user) => ({
          id: user.id,
          full_name: user.full_name,
          email: user.email,
        }));
        normalized.sort((a, b) => a.full_name.localeCompare(b.full_name));
        setOwnerOptions(normalized);
      } catch (error) {
        console.error("Error loading owners:", error);
      } finally {
        setOwnersLoading(false);
      }
    };
    loadOwners();
  }, [open, canAssignOwner]);

  const onSubmit = async (data: ProspectFormData) => {
    setIsLoading(true);
    try {
      const contacts = [] as any[];
      const hasContact =
        (data.contact_name ?? "").trim() ||
        (data.contact_email ?? "").trim() ||
        (data.contact_telephone ?? "").trim();
      if (hasContact) {
        contacts.push({
          name: data.contact_name || null,
          position: data.contact_position || null,
          email: data.contact_email || null,
          telephone: data.contact_telephone || null,
          is_primary: true,
        });
      }

      const ownerId = data.owner_id && data.owner_id !== "unassigned" ? data.owner_id : null;
      const normalizedFinalClientTypes = data.business_type === "final_client"
        ? Array.from(new Set((data.final_client_types || []).map((value) => value.trim()).filter(Boolean)))
        : [];
      await createProspect({
        company_name: data.company_name,
        business_type: data.business_type,
        final_client_types: normalizedFinalClientTypes,
        country: data.country || null,
        address: data.address || null,
        website: data.website || null,
        referred_from: data.referred_from || null,
        owner_id: ownerId,
        contacts: contacts.length ? contacts : [],
      });

      toast({
        title: "Success",
        description: "Prospect created successfully",
      });
      onSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Prospect</DialogTitle>
          <DialogDescription>
            Create a new prospect company with primary contact details.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input {...register("company_name")} placeholder="Company name" />
              {errors.company_name && (
                <p className="text-sm text-destructive">{errors.company_name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Business Type *</Label>
              <Select
                value={selectedBusinessType}
                onValueChange={(value) => {
                  setValue("business_type", value as "operator" | "agency" | "final_client", { shouldDirty: true, shouldValidate: true });
                  if (value !== "final_client") {
                    setValue("final_client_types", [], { shouldDirty: true, shouldValidate: true });
                  }
                }}
              >
                <SelectTrigger className={errors.business_type ? "border-destructive focus-visible:ring-destructive" : undefined}>
                  <SelectValue placeholder="Select business type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="agency">Agency</SelectItem>
                  <SelectItem value="final_client">Final Client</SelectItem>
                </SelectContent>
              </Select>
              {errors.business_type && (
                <p className="text-sm text-destructive">{errors.business_type.message}</p>
              )}
            </div>
            {selectedBusinessType === "final_client" && (
              <div className="space-y-2">
                <Label>Final Client Types *</Label>
                <SearchableSelect
                  items={finalClientTypeOptions.map((option) => ({
                    id: option.value,
                    title: option.label,
                  }))}
                  value={finalClientTypePicker || undefined}
                  onValueChange={(value) => addFinalClientType(String(value) as FinalClientType)}
                  placeholder="Select a final client type"
                  emptyText="No final client type found."
                />
                {finalClientTypes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {finalClientTypes.map((type) => {
                      const label = finalClientTypeOptions.find((option) => option.value === type)?.label || type;
                      return (
                        <Badge key={type} variant="secondary" className="flex items-center gap-1 pr-1">
                          <span>{label}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={() => removeFinalClientType(type)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
                {errors.final_client_types && (
                  <p className="text-sm text-destructive">{errors.final_client_types.message}</p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Country</Label>
                {selectedCountry && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setValue("country", "", { shouldDirty: true, shouldValidate: true })}
                  >
                    Clear
                  </Button>
                )}
              </div>
              <SearchableSelect
                items={COUNTRY_OPTIONS}
                value={selectedCountry || undefined}
                onValueChange={(value) => {
                  setValue("country", String(value), { shouldDirty: true, shouldValidate: true });
                }}
                placeholder="Select a country"
                emptyText="No country found."
                fallbackDisplayValue={selectedCountry || undefined}
              />
              {errors.country && (
                <p className="text-sm text-destructive">{errors.country.message}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {canAssignOwner && (
              <div className="space-y-2">
                <Label>Responsible</Label>
                <Select
                  value={watch("owner_id") || "unassigned"}
                  onValueChange={(value) => setValue("owner_id", value, { shouldDirty: true, shouldValidate: true })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select owner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {ownersLoading && (
                      <SelectItem value="loading" disabled>
                        Loading...
                      </SelectItem>
                    )}
                    {ownerOptions.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Address</Label>
            <Textarea {...register("address")} placeholder="Address" rows={2} />
          </div>

          <div className="space-y-2">
            <Label>Website</Label>
            <Input {...register("website")} placeholder="https://example.com" />
            {errors.website && (
              <p className="text-sm text-destructive">{errors.website.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Referred From</Label>
            <Input {...register("referred_from")} placeholder="Referral source" />
          </div>

          <div className="rounded-lg border border-border p-4 space-y-4">
            <p className="text-sm font-semibold text-foreground">Primary Contact (optional)</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input {...register("contact_name")} placeholder="Contact name" />
              </div>
              <div className="space-y-2">
                <Label>Position</Label>
                <Input {...register("contact_position")} placeholder="Position" />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input {...register("contact_email")} placeholder="Email" />
                {errors.contact_email && (
                  <p className="text-sm text-destructive">{errors.contact_email.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Telephone</Label>
                <Input {...register("contact_telephone")} placeholder="Telephone" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? "Saving..." : "Create Prospect"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProspectForm;
